import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import type { SupportedLanguage } from '../../locales/i18n';
import { getCurrentUser, isAuthenticated, signOut } from '../../services/cognitoService';
import './Header.css';

interface HeaderProps {
  isLoggedIn?: boolean;
}

const Header: React.FC<HeaderProps> = ({ isLoggedIn: externalIsLoggedIn }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage, supportedLanguages } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [internalIsLoggedIn, setInternalIsLoggedIn] = useState(() =>
    externalIsLoggedIn === undefined ? isAuthenticated() : false,
  );
  const langDropdownRef = useRef<HTMLDivElement>(null);
  const profileDropdownRef = useRef<HTMLDivElement>(null);

  const isLoggedIn = externalIsLoggedIn !== undefined ? externalIsLoggedIn : internalIsLoggedIn;

  const [userEmail, setUserEmail] = useState<string | null>(() => {
    if (isLoggedIn) {
      const currentUser = getCurrentUser();
      return currentUser?.email || localStorage.getItem('userEmail') || null;
    }
    return null;
  });

  // Sync user email when logged in state changes
  useEffect(() => {
    if (isLoggedIn) {
      const currentUser = getCurrentUser();
      setUserEmail(currentUser?.email || localStorage.getItem('userEmail') || null);
    } else {
      setUserEmail(null);
    }
  }, [isLoggedIn]);

  const userInitial = userEmail ? userEmail.charAt(0).toUpperCase() : '';

  // Check authentication status if not provided by props
  useEffect(() => {
    if (externalIsLoggedIn === undefined) {
      setInternalIsLoggedIn(isAuthenticated());
    }
  }, [externalIsLoggedIn]);

  // Handle scroll for header background
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target as Node)) {
        setIsLangDropdownOpen(false);
      }
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target as Node)
      ) {
        setIsProfileDropdownOpen(false);
      }
    };
    if (isLangDropdownOpen || isProfileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isLangDropdownOpen, isProfileDropdownOpen]);

  // Handle sign out
  const handleSignOut = () => {
    signOut();
    setIsProfileDropdownOpen(false);
    setIsMenuOpen(false);
    navigate('/');
    window.location.reload(); // Refresh to update auth state
  };

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      <header className={`header ${isScrolled ? 'scrolled' : ''} ${isMenuOpen ? 'menu-open' : ''}`}>
        {/* Logo */}
        <Link to="/" className="header-logo">
          <img src="/logo.png" alt="TheyChat" className="header-logo-img" />
        </Link>

        {/* Desktop Navigation */}
        <nav className="nav-menu desktop-nav">
          <Link to="/" className={`nav-link ${isActive('/') ? 'active' : ''}`}>
            {t('header.home')}
          </Link>
          <Link
            to="/subscription"
            className={`nav-link ${isActive('/subscription') ? 'active' : ''}`}
          >
            {t('header.subscription')}
          </Link>
          <Link to="/sponsor" className={`nav-link ${isActive('/sponsor') ? 'active' : ''}`}>
            {t('header.sponsor')}
          </Link>
        </nav>

        {/* Desktop Actions */}
        <div className="header-actions desktop-actions">
          <div className="lang-selector-container" ref={langDropdownRef}>
            <button
              type="button"
              className={`lang-selector ${isLangDropdownOpen ? 'open' : ''}`}
              onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
              aria-haspopup="listbox"
              aria-expanded={isLangDropdownOpen}
              aria-label={t('common.changeLanguage', 'Change language')}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                aria-hidden="true"
              >
                <title>Language Icon</title>
                <circle cx="12" cy="12" r="10" />
                <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
              <span>{currentLanguage.toUpperCase()}</span>
              <svg
                className="dropdown-arrow"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <title>Dropdown Arrow</title>
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>

            {/* Language Dropdown */}
            {isLangDropdownOpen && (
              <div className="lang-dropdown" role="listbox">
                {Object.entries(supportedLanguages).map(([code, { nativeName }]) => (
                  <button
                    type="button"
                    key={code}
                    className={`lang-option ${currentLanguage === code ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      changeLanguage(code as SupportedLanguage);
                      setIsLangDropdownOpen(false);
                    }}
                    role="option"
                    aria-selected={currentLanguage === code}
                  >
                    {nativeName}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Show different actions based on login state */}
          {isLoggedIn ? (
            <div
              ref={profileDropdownRef}
              className={`profile-selector ${isProfileDropdownOpen ? 'open' : ''}`}
            >
              <button
                type="button"
                className="btn btn-user-avatar"
                onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                aria-label="Profile menu"
                aria-haspopup="true"
                aria-expanded={isProfileDropdownOpen}
                title={t('header.profile', 'Profile')}
              >
                {userInitial || (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <title>{t('header.profile', 'Profile')}</title>
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                )}
              </button>

              {/* Profile Dropdown */}
              {isProfileDropdownOpen && (
                <div className="profile-dropdown">
                  <Link
                    to="/settings"
                    className="profile-dropdown-item"
                    onClick={() => setIsProfileDropdownOpen(false)}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <title>{t('header.settings')}</title>
                      <circle cx="12" cy="12" r="3" />
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                    </svg>
                    <span>{t('header.settings')}</span>
                  </Link>
                  <Link
                    to="/subscription"
                    className="profile-dropdown-item"
                    onClick={() => setIsProfileDropdownOpen(false)}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <title>{t('header.subscription')}</title>
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                      <line x1="1" y1="10" x2="23" y2="10" />
                    </svg>
                    <span>{t('header.subscription')}</span>
                  </Link>
                  <Link
                    to="/about"
                    className="profile-dropdown-item"
                    onClick={() => setIsProfileDropdownOpen(false)}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <title>{t('header.aboutUs')}</title>
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                    <span>{t('header.aboutUs')}</span>
                  </Link>
                  <div className="profile-dropdown-divider"></div>
                  <button
                    type="button"
                    className="profile-dropdown-item signout"
                    onClick={handleSignOut}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <title>{t('header.signOut', 'Sign Out')}</title>
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    <span>{t('header.signOut', 'Sign Out')}</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <button type="button" className="btn btn-outline" onClick={() => navigate('/login')}>
                {t('header.signIn')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate('/register')}
              >
                {t('header.signUp')}
              </button>
            </>
          )}
        </div>

        {/* Hamburger Menu Button */}
        <button
          type="button"
          className={`hamburger-btn ${isMenuOpen ? 'open' : ''}`}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
          aria-expanded={isMenuOpen}
        >
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      <div className={`mobile-menu-overlay ${isMenuOpen ? 'open' : ''}`}>
        <nav className="mobile-nav">
          <Link to="/" className={`mobile-nav-link ${isActive('/') ? 'active' : ''}`}>
            {t('header.home')}
          </Link>
          <Link
            to="/subscription"
            className={`mobile-nav-link ${isActive('/subscription') ? 'active' : ''}`}
          >
            {t('header.subscription')}
          </Link>
          <Link to="/sponsor" className={`mobile-nav-link ${isActive('/sponsor') ? 'active' : ''}`}>
            {t('header.sponsor')}
          </Link>
          {isLoggedIn && (
            <Link
              to="/settings"
              className={`mobile-nav-link ${isActive('/settings') ? 'active' : ''}`}
            >
              {t('header.settings')}
            </Link>
          )}
        </nav>

        <div className="mobile-actions">
          <div className="mobile-lang-selector">
            <span className="mobile-lang-label">{t('common.changeLanguage')}</span>
            <div className="mobile-lang-options">
              {Object.entries(supportedLanguages).map(([code, { nativeName }]) => (
                <button
                  type="button"
                  key={code}
                  className={`mobile-lang-btn ${currentLanguage === code ? 'active' : ''}`}
                  onClick={() => changeLanguage(code as SupportedLanguage)}
                >
                  {nativeName}
                </button>
              ))}
            </div>
          </div>

          {/* Show different actions based on login state */}
          {isLoggedIn ? (
            <button type="button" className="btn btn-signout mobile-btn" onClick={handleSignOut}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <title>{t('header.signOut', 'Sign Out')}</title>
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              {t('header.signOut', 'Sign Out')}
            </button>
          ) : (
            <>
              <button
                type="button"
                className="btn btn-outline mobile-btn"
                onClick={() => navigate('/login')}
              >
                {t('header.signIn')}
              </button>
              <button
                type="button"
                className="btn btn-primary mobile-btn"
                onClick={() => navigate('/register')}
              >
                {t('header.signUp')}
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Header;
